import { Fragment, useEffect, useMemo, useState } from 'react';
import { api, PermissionDef, PermissionProfile, PermissionScope } from '../lib/api';
import { Card, CardHeader, Table, Th, Tr, Td, Badge, ActionButton, Spinner, EmptyState } from '../components/ui';

// ─── Permission profiles ─────────────────────────────────────────────────────
// Definable access for the non-FP&A surface. A profile is a named set of
// permission grants; "Role default" profiles apply automatically to every
// user with that role (editable, not deletable), and any user can carry
// additional profiles (assigned on Users & roles). Admins hold every
// permission via the is_admin flag regardless of profiles. FP&A access is
// NOT managed here — it stays on the fpa_role grant (FP&A access page).
//
// Matrix layout: permissions as rows (grouped by category), profiles as
// columns. SCOPED permissions (reports) render a scope dropdown — the grant
// carries WHOSE data the page shows (self / direct reports / subtree / all);
// unscoped permissions are plain on/off checkboxes.

const inputStyle: React.CSSProperties = {
  fontSize: 11, padding: '3px 6px', border: '0.5px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', color: 'var(--color-text)',
};

const SCOPE_ORDER: PermissionScope[] = ['self', 'direct_reports', 'subtree', 'all'];
const SCOPE_LABELS: Record<PermissionScope, string> = {
  self: 'Self',
  direct_reports: 'Direct reports',
  subtree: 'Subtree',
  all: 'All',
};

/** Compact column label: role defaults show the role, customs their name. */
function profileShortName(p: PermissionProfile): string {
  return p.system_role ?? p.name;
}

function grantsOf(p: PermissionProfile): Map<string, PermissionScope> {
  return new Map(p.permissions.map((g) => [g.key, g.scope]));
}

export default function AdminPermissionProfilesPage() {
  const [permissions, setPermissions] = useState<PermissionDef[] | null>(null);
  const [profiles, setProfiles] = useState<PermissionProfile[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Unsaved grant edits per profile id (key → scope); absent = no pending change.
  const [edits, setEdits] = useState<Record<string, Map<string, PermissionScope>>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);

  async function reload() {
    setError(null);
    try {
      const { permissions: perms, profiles: rows } = await api.admin.permissionProfiles.list();
      setPermissions(perms);
      setProfiles(rows);
      setEdits({});
    } catch (e) {
      setError((e as Error).message);
    }
  }
  useEffect(() => { void reload(); }, []);

  const categories = useMemo(() => {
    const order: string[] = [];
    for (const p of permissions ?? []) if (!order.includes(p.category)) order.push(p.category);
    return order;
  }, [permissions]);

  function currentGrants(p: PermissionProfile): Map<string, PermissionScope> {
    return edits[p.id] ?? grantsOf(p);
  }

  /** Unscoped keys: plain on/off (stored with the inert 'all' scope). */
  function toggle(p: PermissionProfile, key: string) {
    const next = new Map(currentGrants(p));
    if (next.has(key)) next.delete(key); else next.set(key, 'all');
    setEdits((e) => ({ ...e, [p.id]: next }));
  }

  /** Scoped keys: '' clears the grant, otherwise sets its scope. */
  function setGrantScope(p: PermissionProfile, key: string, scope: string) {
    const next = new Map(currentGrants(p));
    if (scope === '') next.delete(key);
    else next.set(key, scope as PermissionScope);
    setEdits((e) => ({ ...e, [p.id]: next }));
  }

  function isDirty(p: PermissionProfile): boolean {
    const edit = edits[p.id];
    if (!edit) return false;
    const orig = grantsOf(p);
    return edit.size !== orig.size || [...edit].some(([k, s]) => orig.get(k) !== s);
  }

  async function save(p: PermissionProfile) {
    setSaving(p.id);
    setError(null);
    try {
      const permissions = [...currentGrants(p)]
        .map(([key, scope]) => ({ key, scope }))
        .sort((a, b) => a.key.localeCompare(b.key));
      await api.admin.permissionProfiles.update(p.id, { permissions });
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(null);
    }
  }

  async function remove(p: PermissionProfile) {
    const carried = p.member_count > 0 ? ` ${p.member_count} user(s) currently carry it and will lose its permissions.` : '';
    if (!window.confirm(`Delete profile “${p.name}”?${carried}`)) return;
    setError(null);
    try {
      await api.admin.permissionProfiles.remove(p.id);
      await reload();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function create() {
    if (!newName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      await api.admin.permissionProfiles.create({
        name: newName.trim(),
        description: newDescription.trim() || null,
        permissions: [],
      });
      setNewName('');
      setNewDescription('');
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCreating(false);
    }
  }

  if (error && !profiles) return <EmptyState message={error} />;
  if (!permissions || !profiles) return <Spinner />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {error && (
        <div style={{ fontSize: 11, color: 'var(--color-red-text)', padding: '6px 2px' }}>{error}</div>
      )}

      <Card>
        <CardHeader>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Permission profiles</div>
        </CardHeader>
        <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
          A profile is a named set of permissions. <b>Role default</b> profiles (shown by role) apply
          automatically to every user with that role — edit them to change what a role can reach, no code
          change needed. Additional profiles are assigned per user on <b>Users &amp; roles</b>; a user's
          access is the union of all their profiles. Report permissions carry a <b>scope</b> — whose data
          the page shows: <b>Self</b>, <b>Direct reports</b>, <b>Subtree</b> (the full reporting line), or{' '}
          <b>All</b> (the whole org); when several profiles grant the same report, the widest scope wins.
          Full admins (<code style={{ fontSize: 10 }}>is_admin</code>) always hold every permission at All.
          FP&amp;A access is managed separately on the FP&amp;A access page and is not affected by profiles.
        </div>
        <div style={{ overflowX: 'auto' }}>
          <Table>
            <thead>
              <tr>
                <Th>Permission</Th>
                {profiles.map((p) => (
                  <Th key={p.id}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 74 }} title={p.description ?? p.name}>
                      <span style={{ whiteSpace: 'nowrap' }}>{profileShortName(p)}</span>
                      {p.system_role
                        ? <Badge variant="blue">role default</Badge>
                        : <span style={{ fontSize: 9, fontWeight: 400, color: 'var(--color-text-muted)' }}>{p.member_count} user(s)</span>}
                      <span style={{ display: 'inline-flex', gap: 4 }}>
                        {isDirty(p) && (
                          <ActionButton variant="primary" onClick={() => save(p)} disabled={saving === p.id}>
                            {saving === p.id ? '…' : 'Save'}
                          </ActionButton>
                        )}
                        {!p.system_role && <ActionButton onClick={() => remove(p)}>✕</ActionButton>}
                      </span>
                    </div>
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => (
                <Fragment key={cat}>
                  <tr>
                    <td colSpan={profiles.length + 1} style={{ padding: '6px 16px', borderTop: '0.5px solid var(--color-border)', background: 'var(--color-surface-sunken, transparent)' }}>
                      <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--color-text-muted)' }}>
                        {cat}
                      </span>
                    </td>
                  </tr>
                  {permissions.filter((perm) => perm.category === cat).map((perm) => (
                    <Tr key={perm.key}>
                      <Td bold>
                        <span title={perm.description} style={{ whiteSpace: 'nowrap' }}>{perm.label}</span>
                      </Td>
                      {profiles.map((p) => (
                        <Td key={p.id}>
                          {perm.scoped ? (
                            <select
                              value={currentGrants(p).get(perm.key) ?? ''}
                              onChange={(e) => setGrantScope(p, perm.key, e.target.value)}
                              title={`${p.name} — ${perm.label}: whose data the report shows`}
                              style={{
                                ...inputStyle, padding: '2px 4px',
                                color: currentGrants(p).has(perm.key) ? 'var(--color-text)' : 'var(--color-text-muted)',
                              }}
                            >
                              <option value="">—</option>
                              {SCOPE_ORDER.map((s) => (
                                <option key={s} value={s}>{SCOPE_LABELS[s]}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="checkbox"
                              checked={currentGrants(p).has(perm.key)}
                              onChange={() => toggle(p, perm.key)}
                              title={`${p.name} — ${perm.label}`}
                            />
                          )}
                        </Td>
                      ))}
                    </Tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </Table>
        </div>
        {/* Create a new assignable profile */}
        <div style={{ padding: '10px 16px', borderTop: '0.5px solid var(--color-border)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            placeholder="New profile name (e.g. Sales ops)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            style={{ ...inputStyle, width: 220 }}
          />
          <input
            placeholder="Description (optional)"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            style={{ ...inputStyle, flex: 1, minWidth: 220 }}
          />
          <ActionButton variant="primary" onClick={create} disabled={creating || !newName.trim()}>
            {creating ? 'Creating…' : 'Create profile'}
          </ActionButton>
        </div>
      </Card>

      {/* Permission legend */}
      <Card>
        <CardHeader>
          <div style={{ fontSize: 13, fontWeight: 500 }}>What each permission grants</div>
        </CardHeader>
        <Table>
          <thead><tr><Th>Permission</Th><Th>Category</Th><Th>Grants</Th></tr></thead>
          <tbody>
            {permissions.map((perm) => (
              <Tr key={perm.key}>
                <Td bold><span style={{ whiteSpace: 'nowrap' }}>{perm.label}</span></Td>
                <Td muted>{perm.category}{perm.scoped ? ' · scoped' : ''}</Td>
                <Td muted>{perm.description}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </Card>
    </div>
  );
}
